import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import tokens from '@verifime/design-tokens';
import {
  TFeatureToggleConfigDetails,
  TFeatureToggleConfigWithResolvedValue,
} from '@verifime/utils';

function FeatureToggleRow({
  name,
  toggleDetails,
  showDivider,
}: Readonly<{
  name: string;
  toggleDetails: TFeatureToggleConfigDetails;
  showDivider: boolean;
}>) {
  return (
    <Box>
      <Stack
        direction={['column', 'row']}
        spacing={tokens.spacingBase}
        gap={tokens.spacingBase}
        justifyContent="space-between"
        sx={{
          bgcolor: toggleDetails.resolvedValue ? 'success.lighter' : 'error.lighter',
          borderRadius: tokens.borderRadiusLg,
          flexWrap: 'wrap',
        }}
      >
        <Stack direction={'row'} spacing={tokens.spacingBase} alignItems="center">
          <Typography variant="subtitle1">{name}</Typography>
          <Chip
            label={toggleDetails.resolvedValue ? 'On' : 'Off'}
            color={toggleDetails.resolvedValue ? 'success' : 'error'}
            size="small"
          />
        </Stack>
        <Stack
          direction={['column', 'column', 'row']}
          spacing={tokens.spacingLg}
          flexWrap="wrap"
          alignItems={['flex-start', 'flex-start', 'center']}
          gap={tokens.spacingBase}
        >
          <Typography variant="body2" color="text.secondary">
            {toggleDetails.env}
          </Typography>
          <Stack>
            <Typography variant="body2" color="text.secondary">
              <strong>Default:</strong> {String(toggleDetails.default)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Configured:</strong> {String(toggleDetails.configuredValue)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Resolved:</strong> {String(toggleDetails.resolvedValue)}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      {showDivider && <Divider sx={{ my: tokens.spacingBase }} />}
    </Box>
  );
}

export default function FeatureToggleList({
  togglesWithConfig,
}: Readonly<{ togglesWithConfig: TFeatureToggleConfigWithResolvedValue }>) {
  const hasNoToggles = Object.keys(togglesWithConfig).length === 0;

  return (
    <Stack gap={tokens.spacing2xl} alignItems="center" width="100%" padding={tokens.spacing2xl}>
      <Typography variant="h4">Feature Toggles</Typography>
      {hasNoToggles && (
        <Typography variant="body1" color="text.secondary">
          No feature toggles configured for this app.
        </Typography>
      )}

      {!hasNoToggles && (
        <Paper elevation={3} sx={{ padding: tokens.spacing2xl }}>
          {Object.entries(togglesWithConfig).map(([key, toggleDetails], idx, arr) => (
            <FeatureToggleRow
              key={key}
              name={key}
              toggleDetails={toggleDetails}
              showDivider={idx < arr.length - 1}
            />
          ))}
        </Paper>
      )}
    </Stack>
  );
}
